/* The command palette. Tokens only (ADR-0007): a colour literal fails stylelint.

   A scrim over the whole shell with one dialog near the top, which is where the eye already is
   after pressing the shortcut. The scrim is `--ink` at partial opacity on a pseudo-element rather
   than a translucent colour function: the stylesheet rule that bans `rgb()` is there so that every
   colour is a token, and a scrim made of a token and an opacity keeps to it. */

.backdrop {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: min(14vh, 120px) 16px 16px;
}

.backdrop::before {
  content: "";
  position: absolute;
  inset: 0;
  background: var(--ink);
  opacity: 0.34;
}

.dialog {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 640px;
  /* The list scrolls inside the dialog; the dialog never outgrows the viewport. */
  max-height: min(560px, 100%);
  min-height: 0;
  background: var(--panel);
  border: 1px solid var(--line-2);
  border-radius: var(--radius);
  box-shadow: var(--shadow-drawer);
}

.field {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 52px;
  flex: 0 0 auto;
  padding: 0 14px;
  border-bottom: 1px solid var(--line);
}

.fieldIcon {
  flex-shrink: 0;
  color: var(--muted);
}

.input {
  flex: 1;
  min-width: 0;
  height: 36px;
  padding: 0 8px;
  border: 0;
  border-radius: var(--radius-ctl);
  background: transparent;
  color: var(--ink);
  font: inherit;
  font-size: var(--t-head);
}

.input::placeholder {
  color: var(--muted);
}

/* The accent ring is kept, drawn on the input itself and inside the row's padding so the dialog's
   frame never clips it. */
.input:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 0;
}

.esc {
  flex-shrink: 0;
  padding: 1px 6px;
  border: 1px solid var(--line-2);
  border-radius: 4px;
  background: var(--panel-2);
  color: var(--muted);
  font: inherit;
  font-size: var(--t-min);
  line-height: 18px;
}

.list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 6px;
  scrollbar-width: thin;
  scrollbar-color: var(--line-2) transparent;
}

.section + .section {
  margin-top: 6px;
}

.heading {
  padding: 8px 10px 4px;
  color: var(--muted);
  font-size: var(--t-min);
  font-weight: 600;
  letter-spacing: 0.06em;
  line-height: 16px;
  text-transform: uppercase;
}

.option {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 36px;
  padding: 6px 10px;
  border-radius: var(--radius-ctl);
  color: var(--ink-2);
  font-size: var(--t-body);
  cursor: pointer;
}

.option:active {
  background: var(--accent-line);
}

/* The highlighted row: a tint, a hairline ring, a left bar and (in the markup) the return arrow.
   Four signals, so it never rests on colour alone. */
.option[data-active="true"] {
  background: var(--accent-w);
  box-shadow: inset 0 0 0 1px var(--accent-line);
  color: var(--accent);
}

.option[data-active="true"]::before {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  width: 3px;
  height: 16px;
  transform: translateY(-50%);
  border-radius: 0 2px 2px 0;
  background: var(--accent);
}

.optionIcon {
  flex-shrink: 0;
  color: var(--muted);
}

.option[data-active="true"] .optionIcon {
  color: var(--accent);
}

.optionLabel {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-weight: 500;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.option[data-active="true"] .optionLabel {
  font-weight: 600;
}

.optionDetail {
  flex-shrink: 0;
  max-width: 40%;
  overflow: hidden;
  color: var(--muted);
  font-size: var(--t-min);
  line-height: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.optionEnter {
  flex-shrink: 0;
  color: var(--accent);
  visibility: hidden;
}

.option[data-active="true"] .optionEnter {
  visibility: visible;
}

.footer {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 16px;
  padding: 8px 14px;
  border-top: 1px solid var(--line);
  border-radius: 0 0 var(--radius) var(--radius);
  background: var(--panel-2);
  color: var(--muted);
  font-size: var(--t-min);
  line-height: 18px;
}

.key {
  display: inline-block;
  min-width: 18px;
  margin-right: 4px;
  padding: 0 4px;
  border: 1px solid var(--line-2);
  border-radius: 4px;
  background: var(--panel);
  color: var(--ink-2);
  font: inherit;
  font-size: var(--t-min);
  line-height: 16px;
  text-align: center;
}

/* A short viewport keeps the dialog on screen and lets the list shrink. */
@media (max-height: 620px) {
  .backdrop {
    padding-top: 8px;
  }
}

/* Phone width: the secondary column and the keyboard hints are the first things to go, because a
   thumb has neither arrow keys nor room for a group name beside the label. */
@media (max-width: 560px) {
  .optionDetail,
  .footer {
    display: none;
  }
}
